import { Controller } from "@hotwired/stimulus"

// Drives the YouTube presenter cockpit. Left/right move between slides; up/down move
// focus between the active slide's beats (stopping at the first/last beat rather than
// spilling into another slide). Space is the linear "clicker": it walks the whole
// deck beat by beat, spilling into the next slide's first beat at a slide's end and
// wrapping from the very last beat back to the first slide. The active slide carries
// `.is-active`; within it the focused beat and its note carry `.is-focused`. The
// Viewer Slide preview is scaled to contain in the bottom third via a CSS var.
export default class extends Controller {
  static targets = ["slide"]
  static values = { slide: { type: Number, default: 0 }, beat: { type: Number, default: 0 } }

  connect() {
    this.fit()
    this._onResize = () => this.fit()
    this._onKey = (event) => this.key(event)
    window.addEventListener("resize", this._onResize)
    window.addEventListener("keydown", this._onKey)
    this.restore()
    this.render()
  }

  // Start where the URL points so a refresh stays put: ?slide=N (1-based) picks the
  // slide, and the #beat-slug hash picks the beat within it.
  restore() {
    const param = new URLSearchParams(window.location.search).get("slide")
    const index = Number.parseInt(param, 10) - 1
    if (Number.isInteger(index)) {
      this.slideValue = Math.max(0, Math.min(index, this.slideTargets.length - 1))
    }
    const slug = decodeURIComponent(window.location.hash.slice(1))
    const beats = [...(this.activeSlide()?.querySelectorAll("[data-beat]") ?? [])]
    const beat = beats.findIndex((el) => el.dataset.beatSlug === slug)
    if (beat >= 0) this.beatValue = beat
  }

  // Reflect the current slide and beat in the URL without adding history entries.
  persist() {
    const url = new URL(window.location.href)
    url.searchParams.set("slide", this.slideValue + 1)
    const slug = this.activeSlide()?.querySelectorAll("[data-beat]")[this.beatValue]?.dataset.beatSlug
    url.hash = slug ? `#${slug}` : ""
    window.history.replaceState({}, "", url)
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("keydown", this._onKey)
  }

  // Scale to fit the whole 1920×1080 frame into the bottom-third preview region.
  fit() {
    const w = window.innerWidth
    const h = window.innerHeight
    this.element.style.setProperty("--scale-preview", Math.min(w / 1920, (h / 3) / 1080))
  }

  key(event) {
    switch (event.key) {
      case " ":
        event.preventDefault()
        this.advance()
        break
      case "ArrowRight":
      case "PageDown":
        event.preventDefault()
        this.goSlide(this.slideValue + 1)
        break
      case "ArrowLeft":
      case "PageUp":
        event.preventDefault()
        this.goSlide(this.slideValue - 1)
        break
      case "ArrowDown":
        event.preventDefault()
        this.goBeat(this.beatValue + 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.goBeat(this.beatValue - 1)
        break
      case "Home":
        event.preventDefault()
        this.goSlide(0)
        break
      case "End":
        event.preventDefault()
        this.goSlide(this.slideTargets.length - 1)
        break
    }
  }

  // Moving to a new slide resets beat focus to its first beat.
  goSlide(index) {
    const last = this.slideTargets.length - 1
    const clamped = Math.max(0, Math.min(index, last))
    if (clamped === this.slideValue) return
    this.slideValue = clamped
    this.beatValue = 0
    this.persist()
    this.render()
  }

  // Space advances one beat, spilling to the next slide's first beat at a slide's end
  // and wrapping from the last beat of the last slide back to slide 1, beat 1. A slide
  // with no beats is a single stop the walk passes straight through.
  advance() {
    const lastBeat = Math.max(0, this.beatCount() - 1)
    if (this.beatValue < lastBeat) {
      this.beatValue += 1
    } else {
      const lastSlide = this.slideTargets.length - 1
      this.slideValue = this.slideValue < lastSlide ? this.slideValue + 1 : 0
      this.beatValue = 0
    }
    this.persist()
    this.render()
  }

  // Up/down stay within the active slide's beats — no spill into another slide.
  goBeat(index) {
    const last = Math.max(0, this.beatCount() - 1)
    this.beatValue = Math.max(0, Math.min(index, last))
    this.persist()
    this.render()
  }

  beatCount() {
    return this.activeSlide()?.querySelectorAll("[data-beat]").length ?? 0
  }

  activeSlide() {
    return this.slideTargets[this.slideValue]
  }

  render() {
    this.slideTargets.forEach((slide, index) => {
      slide.classList.toggle("is-active", index === this.slideValue)
    })
    const active = this.activeSlide()
    if (!active) return
    active.querySelectorAll("[data-beat]").forEach((el, i) => {
      el.classList.toggle("is-focused", i === this.beatValue)
    })
    active.querySelectorAll("[data-note]").forEach((el, i) => {
      el.classList.toggle("is-focused", i === this.beatValue)
    })
  }
}
